import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

// UI Components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// Icons
import { GraduationCap, Plus, TrendingUp, BookOpen, Calendar, Users, Edit, Eye, Download, Upload, FileSpreadsheet } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GradeManagement = ({ user }) => {
  const [assessments, setAssessments] = useState([]);
  const [grades, setGrades] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);  // Liste des enseignants
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('assessments');
  
  // Assessment Dialog
  const [assessmentDialogOpen, setAssessmentDialogOpen] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  
  // Grade Dialog
  const [gradeDialogOpen, setGradeDialogOpen] = useState(false);
  
  const [assessmentForm, setAssessmentForm] = useState({
    title: '',
    description: '',
    assessment_type: '',
    max_grade: 20.0,
    coefficient: 1.0,
    assessment_date: '',
    subject_id: '',
    class_id: '',
    teacher_id: ''  // Enseignant responsable
  });

  const [gradeForm, setGradeForm] = useState({
    assessment_id: '',
    student_grades: [] // [{student_id, grade, comments}]
  });

  const assessmentTypes = [
    { value: 'HOMEWORK', label: 'Devoir maison' },
    { value: 'TEST', label: 'Contrôle' },
    { value: 'EXAM', label: 'Examen' },
    { value: 'ORAL', label: 'Oral' },
    { value: 'PRACTICAL', label: 'Travaux pratiques' }
  ];

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'grades' || user?.role === 'STUDENT' || user?.role === 'PARENT') {
      fetchGrades();
    }
  }, [activeTab, user?.role]);

  // Initialiser le formulaire de notes quand le dialog s'ouvre et que les étudiants sont chargés
  useEffect(() => {
    if (gradeDialogOpen && selectedAssessment && students.length > 0) {
      setGradeForm(prev => ({
        assessment_id: selectedAssessment.id,
        existing_grades: prev.existing_grades || {},
        student_grades: students.map(student => {
          const existingGrade = prev.existing_grades?.[student.id];
          return {
            student_id: student.id,
            student_name: `${student.first_name} ${student.last_name}`,
            grade: existingGrade?.grade || '',
            comments: existingGrade?.comments || ''
          };
        })
      }));
    }
  }, [gradeDialogOpen, students, selectedAssessment]);

  const fetchInitialData = async () => {
    try {
      const promises = [];
      
      promises.push(axios.get(`${API}/assessments`));
      
      if (user?.role && user.role !== 'STUDENT' && user.role !== 'PARENT') {
        promises.push(
          axios.get(`${API}/classes`),
          axios.get(`${API}/subjects`)
        );
        
        // Seuls les admins/principals/secrétaires peuvent voir la liste des enseignants
        if (user.role === 'ADMIN' || user.role === 'PRINCIPAL' || user.role === 'SECRETARY') {
          promises.push(axios.get(`${API}/users?role=TEACHER`));
        }
      }
      
      const results = await Promise.all(promises);
      
      setAssessments(results[0].data);
      
      if (results.length > 1) {
        setClasses(results[1].data);
        setSubjects(results[2].data);
        // Pour les admins/principals/secrétaires, charger la liste des enseignants
        if (user?.role === 'ADMIN' || user?.role === 'PRINCIPAL' || user?.role === 'SECRETARY') {
          setTeachers(results[3]?.data || []);
        }
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      console.error('Error fetching initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGrades = async () => {
    try {
      let response;
      if (user?.role === 'STUDENT' || user?.role === 'PARENT') {
        response = await axios.get(`${API}/grades/my-grades`);
      } else if (user?.role) {
        // Pour les enseignants et admins, récupérer toutes les notes
        response = await axios.get(`${API}/grades`);
      }
      if (response) {
        setGrades(response.data);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des notes');
      console.error('Error fetching grades:', error);
    }
  };

  const fetchStudentsForClass = async (classId, level = null) => {
    try {
      // Utiliser l'endpoint /students qui retourne les profils étudiants avec class_id
      const response = await axios.get(`${API}/students`);
      
      // Filtrer selon la classe ou le niveau de l'évaluation
      let filteredStudents = response.data;
      
      if (classId) {
        // Filtrer par class_id pour les évaluations basées sur une classe
        filteredStudents = response.data.filter(student => student.class_id === classId);
      } else if (level) {
        // Pour les évaluations par niveau, nous devons d'abord récupérer les classes
        // qui correspondent à ce niveau, puis filtrer les étudiants
        try {
          const classesResponse = await axios.get(`${API}/classes`);
          const matchingClasses = classesResponse.data.filter(cls => cls.grade === level);
          const matchingClassIds = matchingClasses.map(cls => cls.id);
          
          filteredStudents = response.data.filter(student => 
            matchingClassIds.includes(student.class_id)
          );
        } catch (classError) {
          console.error('Error fetching classes for level filtering:', classError);
          filteredStudents = [];
        }
      }
      
      setStudents(filteredStudents);
      
      if (filteredStudents.length === 0) {
        toast.info('Aucun étudiant trouvé pour cette classe/niveau');
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des étudiants');
      console.error('Error fetching students:', error);
    }
  };

  const handleCreateAssessment = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/assessments`, {
        ...assessmentForm,
        assessment_date: new Date(assessmentForm.assessment_date).toISOString(),
        max_grade: parseFloat(assessmentForm.max_grade),
        coefficient: parseFloat(assessmentForm.coefficient)
      });
      
      toast.success('Évaluation créée avec succès');
      resetAssessmentForm();
      fetchInitialData();
      setAssessmentDialogOpen(false);
    } catch (error) {
      toast.error('Erreur lors de la création de l\'évaluation');
      console.error('Error creating assessment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGradeSubmission = async () => {
    setLoading(true);
    
    try {
      const promises = gradeForm.student_grades.map(studentGrade => {
        const existingGrade = gradeForm.existing_grades?.[studentGrade.student_id];
        
        if (existingGrade && existingGrade.grade_id) {
          // Mettre à jour une note existante
          return axios.put(`${API}/grades/${existingGrade.grade_id}`, {
            grade: studentGrade.grade ? parseFloat(studentGrade.grade) : null,
            comments: studentGrade.comments
          });
        } else {
          // Créer une nouvelle note
          return axios.post(`${API}/grades`, {
            assessment_id: gradeForm.assessment_id,
            student_id: studentGrade.student_id,
            grade: studentGrade.grade ? parseFloat(studentGrade.grade) : null,
            comments: studentGrade.comments
          });
        }
      });
      
      await Promise.all(promises);
      
      toast.success('Notes enregistrées avec succès');
      setGradeDialogOpen(false);
      fetchGrades();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement des notes');
      console.error('Error saving grades:', error);
    } finally {
      setLoading(false);
    }
  };

  // Excel Import/Export Functions
  const handleDownloadTemplate = async (assessmentId) => {
    try {
      const response = await axios.get(`${API}/grades/template/${assessmentId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'notes_template.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Template téléchargé avec succès');
    } catch (error) {
      toast.error('Erreur lors du téléchargement du template');
      console.error('Error downloading template:', error);
    }
  };

  const handleImportExcel = async (assessmentId, file) => {
    if (!file) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Le fichier doit être au format Excel (.xlsx ou .xls)');
      return;
    }
    
    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/grades/import/${assessmentId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      // Display results
      const { imported_count, errors_count, errors } = response.data;
      
      if (errors_count > 0) {
        toast.warning(
          `Import partiel: ${imported_count} notes importées, ${errors_count} erreurs détectées`,
          { duration: 5000 }
        );
        
        // Show errors details
        errors.forEach((error, index) => {
          if (index < 3) {  // Show only first 3 errors
            toast.error(`Ligne ${error.row}: ${error.error}`, { duration: 4000 });
          }
        });
        
        if (errors.length > 3) {
          toast.info(`... et ${errors.length - 3} autres erreurs`);
        }
      } else {
        toast.success(`${imported_count} notes importées avec succès !`);
      }
      
      // Refresh grades list
      fetchGrades();
    } catch (error) {
      const errorMessage = error.response?.data?.detail?.message || 
                          error.response?.data?.detail ||
                          'Erreur lors de l\'import du fichier';
      toast.error(errorMessage);
      console.error('Error importing Excel:', error);
      
      // If there are validation errors, show them
      if (error.response?.data?.detail?.errors) {
        const errors = error.response.data.detail.errors;
        errors.slice(0, 3).forEach(err => {
          toast.error(`${err.row ? `Ligne ${err.row}: ` : ''}${err.error}`, { duration: 5000 });
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const resetAssessmentForm = () => {
    setAssessmentForm({
      title: '',
      description: '',
      assessment_type: '',
      max_grade: 20.0,
      coefficient: 1.0,
      assessment_date: '',
      subject_id: '',
      class_id: '',
      teacher_id: ''
    });
  };

  const openGradeDialog = async (assessment) => {
    setSelectedAssessment(assessment);
    
    // Charger les étudiants de la classe/niveau de cette évaluation
    await fetchStudentsForClass(assessment.class_id, assessment.level);
    
    // Charger les notes existantes pour cette évaluation
    try {
      const gradesResponse = await axios.get(`${API}/grades?assessment_id=${assessment.id}`);
      const existingGrades = gradesResponse.data;
      
      // Créer un map des notes existantes par student_id
      const gradesMap = {};
      existingGrades.forEach(grade => {
        gradesMap[grade.student_id] = {
          grade_id: grade.id,
          grade: grade.grade,
          comments: grade.comments
        };
      });
      
      // Mettre à jour le formulaire avec les notes existantes
      setGradeForm(prev => ({
        ...prev,
        assessment_id: assessment.id,
        existing_grades: gradesMap
      }));
    } catch (error) {
      console.error('Error loading existing grades:', error);
    }
    
    // Note: students sera mis à jour dans fetchStudentsForClass
    // On doit attendre un peu pour que l'état soit mis à jour
    setTimeout(() => {
      setGradeDialogOpen(true);
    }, 100);
  };

  const getSubjectName = (subjectId) => {
    const subject = subjects.find(s => s.id === subjectId);
    return subject ? subject.name : 'Matière inconnue';
  };

  const getClassName = (classId) => {
    const classObj = classes.find(c => c.id === classId);
    return classObj ? classObj.name : 'Classe inconnue';
  };

  const getAssessmentTypeLabel = (type) => {
    const assessmentType = assessmentTypes.find(t => t.value === type);
    return assessmentType ? assessmentType.label : type;
  };

  const calculateAverage = (studentGrades) => {
    if (!studentGrades || studentGrades.length === 0) return 0;
    const validGrades = studentGrades.filter(g => g.grade !== null);
    if (validGrades.length === 0) return 0;
    const sum = validGrades.reduce((acc, g) => acc + g.grade, 0);
    return (sum / validGrades.length).toFixed(2);
  };

  const canManageGrades = user?.role === 'TEACHER' || user?.role === 'ADMIN' || user?.role === 'PRINCIPAL' || user?.role === 'SECRETARY';

  if (loading && assessments.length === 0) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 fade-in" data-testid="grade-management">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <GraduationCap className="w-6 h-6 mr-2 text-blue-600" />
            {user?.role === 'STUDENT' || user?.role === 'PARENT' ? 'Mes Notes' : 'Gestion des Notes'}
          </h1>
          <p className="text-gray-600 mt-1">
            {user?.role === 'STUDENT' || user?.role === 'PARENT' 
              ? 'Consultez vos notes et évaluations' 
              : 'Créez des évaluations et saisissez les notes'
            }
          </p>
        </div>
        
        {canManageGrades && (
          <Dialog open={assessmentDialogOpen} onOpenChange={setAssessmentDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid="add-assessment-btn"
                onClick={() => { resetAssessmentForm(); setAssessmentDialogOpen(true); }}
                className="btn-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle Évaluation
              </Button>
            </DialogTrigger>
            
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Créer une évaluation</DialogTitle>
                <DialogDescription>
                  Créez une nouvelle évaluation pour vos élèves
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleCreateAssessment} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Titre de l'évaluation</Label>
                  <Input
                    id="title"
                    data-testid="assessment-title-input"
                    value={assessmentForm.title}
                    onChange={(e) => setAssessmentForm({ ...assessmentForm, title: e.target.value })}
                    placeholder="ex: Contrôle Chapitre 1"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Description (optionnelle)</Label>
                  <Textarea
                    id="description"
                    value={assessmentForm.description}
                    onChange={(e) => setAssessmentForm({ ...assessmentForm, description: e.target.value })}
                    placeholder="Description de l'évaluation..."
                    rows={2}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="assessment_type">Type</Label>
                    <Select 
                      value={assessmentForm.assessment_type} 
                      onValueChange={(value) => setAssessmentForm({ ...assessmentForm, assessment_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Type d'évaluation" />
                      </SelectTrigger>
                      <SelectContent>
                        {assessmentTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="assessment_date">Date</Label>
                    <Input
                      id="assessment_date"
                      type="date"
                      value={assessmentForm.assessment_date}
                      onChange={(e) => setAssessmentForm({ ...assessmentForm, assessment_date: e.target.value })}
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="max_grade">Note sur</Label>
                    <Input
                      id="max_grade"
                      type="number"
                      step="0.5"
                      min="1"
                      max="100"
                      value={assessmentForm.max_grade}
                      onChange={(e) => setAssessmentForm({ ...assessmentForm, max_grade: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="coefficient">Coefficient</Label>
                    <Input
                      id="coefficient"
                      type="number"
                      step="0.1"
                      min="0.1"
                      max="10"
                      value={assessmentForm.coefficient}
                      onChange={(e) => setAssessmentForm({ ...assessmentForm, coefficient: e.target.value })}
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="class_id">Classe</Label>
                    <Select 
                      value={assessmentForm.class_id} 
                      onValueChange={(value) => {
                        setAssessmentForm({ ...assessmentForm, class_id: value });
                        fetchStudentsForClass(value);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionnez une classe" />
                      </SelectTrigger>
                      <SelectContent>
                        {classes.map((classObj) => (
                          <SelectItem key={classObj.id} value={classObj.id}>
                            {classObj.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="subject_id">Matière</Label>
                    <Select 
                      value={assessmentForm.subject_id} 
                      onValueChange={(value) => setAssessmentForm({ ...assessmentForm, subject_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionnez une matière" />
                      </SelectTrigger>
                      <SelectContent>
                        {subjects.map((subject) => (
                          <SelectItem key={subject.id} value={subject.id}>
                            {subject.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Enseignant responsable - Visible seulement pour Admin/Principal */}
                {(user?.role === 'ADMIN' || user?.role === 'PRINCIPAL') && (
                  <div className="space-y-2">
                    <Label htmlFor="teacher_id">Enseignant responsable (optionnel)</Label>
                    <Select 
                      value={assessmentForm.teacher_id || "none"} 
                      onValueChange={(value) => setAssessmentForm({ 
                        ...assessmentForm, 
                        teacher_id: value === "none" ? "" : value 
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionnez un enseignant" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucun enseignant spécifique</SelectItem>
                        {teachers.map((teacher) => (
                          <SelectItem key={teacher.id} value={teacher.id}>
                            {teacher.first_name} {teacher.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">
                      Seul cet enseignant pourra saisir les notes de cette évaluation
                    </p>
                  </div>
                )}
                
                <div className="flex justify-end space-x-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setAssessmentDialogOpen(false)}
                  >
                    Annuler
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="btn-primary"
                  >
                    {loading ? 'Création...' : 'Créer l\'évaluation'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="assessments">
            {canManageGrades ? 'Évaluations' : 'Évaluations'}
          </TabsTrigger>
          <TabsTrigger value="grades">
            {user.role === 'STUDENT' || user.role === 'PARENT' ? 'Mes Notes' : 'Notes'}
          </TabsTrigger>
        </TabsList>
        
        {/* Assessments Tab */}
        <TabsContent value="assessments" className="space-y-4">
          {assessments.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {assessments.map((assessment) => (
                <Card key={assessment.id} className="card">
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{assessment.title}</CardTitle>
                        <div className="flex items-center space-x-2 mt-2">
                          <Badge className="badge-primary">
                            {getAssessmentTypeLabel(assessment.assessment_type)}
                          </Badge>
                          <Badge className="badge-secondary">
                            /{assessment.max_grade}
                          </Badge>
                        </div>
                      </div>
                      
                      {canManageGrades && (
                        <div className="flex space-x-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openGradeDialog(assessment)}
                            className="h-8 w-8 p-0"
                            title="Saisir les notes"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Matière:</span>
                        <span className="font-medium">{getSubjectName(assessment.subject_id)}</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">Classe:</span>
                        <span className="font-medium">{getClassName(assessment.class_id)}</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">Date:</span>
                        <span className="font-medium">
                          {new Date(assessment.assessment_date).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">Coefficient:</span>
                        <span className="font-medium">{assessment.coefficient}</span>
                      </div>
                      
                      {assessment.description && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-gray-700 text-xs">{assessment.description}</p>
                        </div>
                      )}
                      
                      {/* Excel Import/Export Buttons */}
                      {canManageGrades && (
                        <div className="mt-4 pt-3 border-t space-y-2">
                          <Button
                            onClick={() => openGradeDialog(assessment)}
                            variant="default"
                            size="sm"
                            className="w-full"
                            title="Saisir les notes"
                          >
                            <TrendingUp className="w-4 h-4 mr-2" />
                            Saisir les notes
                          </Button>
                          
                          <Button
                            onClick={() => handleDownloadTemplate(assessment.id)}
                            variant="outline"
                            size="sm"
                            className="w-full"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Télécharger Template Excel
                          </Button>
                          
                          <div className="relative">
                            <input
                              type="file"
                              id={`excel-upload-${assessment.id}`}
                              accept=".xlsx,.xls"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  handleImportExcel(assessment.id, file);
                                  e.target.value = '';  // Reset input
                                }
                              }}
                              className="hidden"
                            />
                            <Button
                              onClick={() => document.getElementById(`excel-upload-${assessment.id}`).click()}
                              variant="outline"
                              size="sm"
                              className="w-full"
                            >
                              <Upload className="w-4 h-4 mr-2" />
                              Importer depuis Excel
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune évaluation</h3>
              <p className="text-gray-600 mb-6">
                {canManageGrades 
                  ? 'Commencez par créer une évaluation pour vos élèves.'
                  : 'Aucune évaluation n\'est disponible pour le moment.'
                }
              </p>
            </div>
          )}
        </TabsContent>
        
        {/* Grades Tab */}
        <TabsContent value="grades" className="space-y-4">
          {grades.length > 0 ? (
            <div className="space-y-4">
              {/* Grade Statistics for Students/Parents */}
              {(user?.role === 'STUDENT' || user?.role === 'PARENT') && (
                <Card className="card">
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <TrendingUp className="w-5 h-5 mr-2 text-green-600" />
                      Statistiques
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">{grades.length}</div>
                        <div className="text-sm text-gray-600">Notes total</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">
                          {calculateAverage(grades)}
                        </div>
                        <div className="text-sm text-gray-600">Moyenne générale</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-orange-600">
                          {Math.max(...grades.filter(g => g.grade !== null).map(g => g.grade)) || 0}
                        </div>
                        <div className="text-sm text-gray-600">Meilleure note</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-red-600">
                          {Math.min(...grades.filter(g => g.grade !== null).map(g => g.grade)) || 0}
                        </div>
                        <div className="text-sm text-gray-600">Note la plus basse</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Grade List */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {grades.map((grade) => (
                  <Card key={grade.id} className="card">
                    <CardContent className="pt-6">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">
                            Note: {grade.grade !== null ? `${grade.grade}/${grade.max_grade || 20}` : 'Absent'}
                          </h3>
                          <p className="text-sm text-gray-600 mt-1">
                            {grade.assessment_title || `Évaluation #${grade.assessment_id}`}
                          </p>
                          {grade.comments && (
                            <p className="text-sm text-gray-700 mt-2 italic">
                              &quot;{grade.comments}&quot;
                            </p>
                          )}
                          <p className="text-xs text-gray-500 mt-3">
                            Créé le {new Date(grade.created_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                        <div className={`text-right ${
                          grade.grade !== null 
                            ? grade.grade >= 10 ? 'text-green-600' : 'text-red-600'
                            : 'text-gray-500'
                        }`}>
                          <div className="text-2xl font-bold">
                            {grade.grade !== null ? grade.grade.toFixed(1) : 'ABS'}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <GraduationCap className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune note</h3>
              <p className="text-gray-600">
                {user.role === 'STUDENT' || user.role === 'PARENT'
                  ? 'Aucune note n\'est disponible pour le moment.'
                  : 'Les notes apparaîtront ici une fois saisies.'
                }
              </p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Grade Input Dialog */}
      {gradeDialogOpen && selectedAssessment && (
        <Dialog open={gradeDialogOpen} onOpenChange={setGradeDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Saisir les notes - {selectedAssessment.title}</DialogTitle>
              <DialogDescription>
                Évaluation du {new Date(selectedAssessment.assessment_date).toLocaleDateString('fr-FR')} 
                - Note sur {selectedAssessment.max_grade}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {gradeForm.student_grades.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>Aucun étudiant trouvé pour cette classe/niveau.</p>
                  <p className="text-sm mt-2">Vérifiez que des étudiants sont inscrits dans cette classe.</p>
                </div>
              ) : (
                gradeForm.student_grades.map((studentGrade, index) => (
                <div key={studentGrade.student_id} className="flex items-center space-x-4 p-3 border rounded">
                  <div className="flex-1">
                    <span className="font-medium">{studentGrade.student_name}</span>
                  </div>
                  
                  <div className="w-24">
                    <Input
                      type="number"
                      step="0.25"
                      min="0"
                      max={selectedAssessment.max_grade}
                      placeholder="Note"
                      value={studentGrade.grade}
                      onChange={(e) => {
                        const newGrades = [...gradeForm.student_grades];
                        newGrades[index].grade = e.target.value;
                        setGradeForm({ ...gradeForm, student_grades: newGrades });
                      }}
                    />
                  </div>
                  
                  <div className="flex-1">
                    <Input
                      placeholder="Commentaire (optionnel)"
                      value={studentGrade.comments}
                      onChange={(e) => {
                        const newGrades = [...gradeForm.student_grades];
                        newGrades[index].comments = e.target.value;
                        setGradeForm({ ...gradeForm, student_grades: newGrades });
                      }}
                    />
                  </div>
                </div>
              ))
              )}
              
              <div className="flex justify-end space-x-3">
                <Button 
                  variant="outline" 
                  onClick={() => setGradeDialogOpen(false)}
                >
                  Annuler
                </Button>
                <Button 
                  onClick={handleGradeSubmission}
                  disabled={loading}
                  className="btn-primary"
                >
                  {loading ? 'Enregistrement...' : 'Enregistrer les notes'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default GradeManagement;